import React, { useEffect, useMemo, useRef, useState } from "react";
import { AppFrame, TopBar, BottomNav } from "@/components/Shell";
import { api, resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { compressImage } from "@/lib/image";
import {
  Plus,
  Trash2,
  Pencil,
  Check,
  X,
  Image as ImageIcon,
  Loader2,
  ChevronRight,
  ChevronDown,
} from "lucide-react";

const FALLBACK = {
  "Artificial Jewellery": "https://images.unsplash.com/photo-1705326454924-f6777522b030?w=200&h=200&fit=crop",
  "Ladies Handbags": "https://images.unsplash.com/photo-1683921590274-a83862cb11c3?w=200&h=200&fit=crop",
  "Cosmetics": "https://images.unsplash.com/photo-1625093742435-6fa192b6fb10?w=200&h=200&fit=crop",
  "Ladies Accessories": "https://images.unsplash.com/photo-1542452255191-c85a98f2c5d1?w=200&h=200&fit=crop",
};

// Build depth + parent path for each category. Returns list ordered by DFS with depth info.
const buildFlatTree = (list) => {
  const childrenMap = {};
  list.forEach((c) => {
    const pid = c.parent_id || "__root__";
    (childrenMap[pid] = childrenMap[pid] || []).push(c);
  });
  Object.values(childrenMap).forEach((arr) =>
    arr.sort((a, b) => (a.order || 0) - (b.order || 0) || a.name.localeCompare(b.name)),
  );
  const out = [];
  const walk = (pid, depth, ancestors) => {
    const kids = childrenMap[pid] || [];
    kids.forEach((c) => {
      const childKids = childrenMap[c.id] || [];
      out.push({
        cat: c,
        depth,
        hasChildren: childKids.length > 0,
        childCount: childKids.length,
        ancestors,
      });
      walk(c.id, depth + 1, [...ancestors, c.id]);
    });
  };
  walk("__root__", 0, []);
  return out;
};

export default function ManageCategories() {
  const [cats, setCats] = useState([]);
  const [newName, setNewName] = useState("");
  const [newParent, setNewParent] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [uploadingId, setUploadingId] = useState(null);
  const [collapsed, setCollapsed] = useState({}); // id -> true means hidden
  const fileInputRef = useRef(null);
  const uploadTargetRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/categories");
    setCats(data);
  };
  useEffect(() => {
    load();
  }, []);

  const flat = useMemo(() => buildFlatTree(cats), [cats]);

  // Filter out rows whose any ancestor is collapsed
  const visible = useMemo(
    () => flat.filter((row) => !row.ancestors.some((aid) => collapsed[aid])),
    [flat, collapsed],
  );

  const add = async () => {
    if (!newName.trim()) return;
    try {
      await api.post("/categories", { name: newName.trim(), parent_id: newParent || null });
      toast.success("Category added");
      // Ensure the parent is expanded so the new child is visible
      if (newParent) {
        setCollapsed((s) => ({ ...s, [newParent]: false }));
      }
      setNewName("");
      setNewParent("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add category");
    }
  };

  const save = async (id) => {
    const cat = cats.find((c) => c.id === id);
    await api.put(`/categories/${id}`, {
      name: editName,
      parent_id: cat?.parent_id || null,
      image: cat?.image || null,
    });
    setEditId(null);
    toast.success("Saved");
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this category (and its sub-categories)?")) return;
    try {
      await api.delete(`/categories/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const openUpload = (cat) => {
    uploadTargetRef.current = cat;
    fileInputRef.current?.click();
  };

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    const cat = uploadTargetRef.current;
    e.target.value = "";
    if (!file || !cat) return;
    setUploadingId(cat.id);
    try {
      const optimized = await compressImage(file, { maxEdge: 800, quality: 0.85 });
      const fd = new FormData();
      fd.append("file", optimized);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put(`/categories/${cat.id}`, {
        name: cat.name,
        parent_id: cat.parent_id || null,
        image: data.url,
      });
      toast.success("Thumbnail updated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingId(null);
    }
  };

  const removeImage = async (cat) => {
    if (!window.confirm("Remove this thumbnail?")) return;
    await api.put(`/categories/${cat.id}`, {
      name: cat.name,
      parent_id: cat.parent_id || null,
      image: null,
    });
    toast.success("Removed");
    load();
  };

  const toggleCollapsed = (id) => setCollapsed((s) => ({ ...s, [id]: !s[id] }));

  return (
    <AppFrame>
      <TopBar title="Categories" subtitle="Manage" />
      <div className="px-5 mt-4 space-y-3">
        {/* Add form */}
        <div
          className="rounded-2xl p-3 space-y-2"
          style={{ background: "var(--paper)", border: "1px solid rgba(27,25,23,0.06)" }}
        >
          <input
            data-testid="new-category-name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New category name"
            className="w-full h-11 rounded-xl px-3 text-sm outline-none"
            style={{ border: "1px solid rgba(27,25,23,0.15)" }}
          />
          <select
            data-testid="new-category-parent"
            value={newParent}
            onChange={(e) => setNewParent(e.target.value)}
            className="w-full h-11 rounded-xl px-3 text-sm outline-none bg-white"
            style={{ border: "1px solid rgba(27,25,23,0.15)" }}
          >
            <option value="">Top-level category</option>
            {flat.map(({ cat, depth }) => (
              <option key={cat.id} value={cat.id}>
                {"— ".repeat(depth + 1)}Under: {cat.name}
              </option>
            ))}
          </select>
          <button
            data-testid="add-category-btn"
            onClick={add}
            className="w-full h-11 rounded-full text-[11px] tracking-editorial-sm uppercase press flex items-center justify-center gap-2"
            style={{ background: "var(--ink)", color: "var(--bg)" }}
          >
            <Plus size={14} /> Add category
          </button>
          <p className="text-[10px]" style={{ color: "var(--ink-3)" }}>
            Tip: You can nest to any depth (e.g. Ladies Accessories → Hair Accessories → Clutchers → Stone Clutchers).
          </p>
        </div>

        {/* Flat rendering with indent — no recursive components */}
        <div
          className="mt-4 rounded-2xl p-2"
          style={{ background: "var(--paper)", border: "1px solid rgba(27,25,23,0.06)" }}
        >
          {visible.length === 0 && (
            <div className="py-8 text-center text-xs" style={{ color: "var(--ink-3)" }}>
              No categories yet. Add one above.
            </div>
          )}
          {visible.map((row) => {
            const { cat, depth, hasChildren, childCount } = row;
            const editing = editId === cat.id;
            const isTop = depth === 0;
            const isCollapsed = !!collapsed[cat.id];
            const thumb = cat.image ? resolveImage(cat.image) : isTop ? FALLBACK[cat.name] : null;
            return (
              <div
                key={cat.id}
                className="flex items-center gap-2 py-2"
                style={{
                  paddingLeft: 4 + depth * 20,
                  borderTop: depth === 0 ? "1px solid rgba(27,25,23,0.06)" : "1px dashed rgba(27,25,23,0.05)",
                }}
              >
                <button
                  data-testid={`cat-toggle-${cat.id}`}
                  onClick={() => toggleCollapsed(cat.id)}
                  className="w-6 h-6 grid place-items-center press shrink-0"
                  style={{
                    color: "var(--ink-3)",
                    visibility: hasChildren ? "visible" : "hidden",
                  }}
                  aria-label={isCollapsed ? "Expand" : "Collapse"}
                >
                  {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                </button>

                {/* Thumbnail — available at every level now */}
                <div className="relative shrink-0">
                  <button
                    data-testid={`cat-thumb-${cat.id}`}
                    onClick={() => openUpload(cat)}
                    className={`${isTop ? "w-11 h-11" : "w-9 h-9"} rounded-xl overflow-hidden press relative`}
                    style={{ background: "#F1E8D8", border: "1px solid rgba(27,25,23,0.08)" }}
                    aria-label="Change thumbnail"
                  >
                    {thumb ? (
                      <img src={thumb} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full grid place-items-center" style={{ color: "var(--ink-3)" }}>
                        <ImageIcon size={isTop ? 14 : 12} strokeWidth={1.5} />
                      </div>
                    )}
                    {uploadingId === cat.id && (
                      <div className="absolute inset-0 bg-black/40 grid place-items-center text-white">
                        <Loader2 size={14} className="animate-spin" />
                      </div>
                    )}
                  </button>
                  {cat.image && (
                    <button
                      data-testid={`cat-thumb-remove-${cat.id}`}
                      onClick={() => removeImage(cat)}
                      className="absolute -top-1 -right-1 w-5 h-5 rounded-full grid place-items-center press"
                      style={{ background: "var(--ink)", color: "var(--bg)" }}
                      aria-label="Remove image"
                    >
                      <X size={10} />
                    </button>
                  )}
                </div>

                {editing ? (
                  <>
                    <input
                      data-testid={`edit-cat-input-${cat.id}`}
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="flex-1 h-10 rounded-xl px-3 text-sm outline-none"
                      style={{ border: "1px solid rgba(27,25,23,0.15)" }}
                    />
                    <button
                      data-testid={`edit-cat-save-${cat.id}`}
                      onClick={() => save(cat.id)}
                      className="w-9 h-9 rounded-full grid place-items-center press"
                      style={{ background: "var(--ink)", color: "var(--bg)" }}
                    >
                      <Check size={14} />
                    </button>
                    <button
                      data-testid={`edit-cat-cancel-${cat.id}`}
                      onClick={() => setEditId(null)}
                      className="w-9 h-9 rounded-full grid place-items-center press"
                      style={{ border: "1px solid rgba(27,25,23,0.15)" }}
                    >
                      <X size={14} />
                    </button>
                  </>
                ) : (
                  <>
                    <div
                      className={`flex-1 min-w-0 ${isTop ? "font-display text-[16px]" : "text-sm"}`}
                      style={{ color: "var(--ink)" }}
                    >
                      <span className="truncate inline-block max-w-full align-bottom">{cat.name}</span>
                      {hasChildren && (
                        <span
                          className="ml-2 text-[10px] tracking-editorial-sm uppercase px-1.5 py-0.5 rounded-full align-middle"
                          style={{ background: "var(--bg-alt)", color: "var(--ink-3)" }}
                        >
                          {childCount}
                        </span>
                      )}
                    </div>
                    <button
                      data-testid={`edit-cat-${cat.id}`}
                      onClick={() => {
                        setEditId(cat.id);
                        setEditName(cat.name);
                      }}
                      className="w-9 h-9 rounded-full grid place-items-center press"
                      style={{ border: "1px solid rgba(27,25,23,0.12)" }}
                    >
                      <Pencil size={14} strokeWidth={1.6} />
                    </button>
                    <button
                      data-testid={`del-cat-${cat.id}`}
                      onClick={() => del(cat.id)}
                      className="w-9 h-9 rounded-full grid place-items-center press"
                      style={{ border: "1px solid rgba(27,25,23,0.12)", color: "var(--accent)" }}
                    >
                      <Trash2 size={14} strokeWidth={1.6} />
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onFileChange}
        />
      </div>
      <BottomNav />
    </AppFrame>
  );
}
